import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
         BarChart, Bar, ReferenceLine } from 'recharts';
import FilterBar from './shared/FilterBar';
import ChangelistDates from './shared/ChangelistDates';
import './PerformanceTrends.css';

const METRIC_OPTIONS = [
  { value: 'fps', label: 'FPS', unit: 'fps', higherIsBetter: true },
  { value: 'memory', label: 'Memory (MB)', unit: 'MB', higherIsBetter: false },
  { value: 'cpu', label: 'CPU Frame Time', unit: 'ms', higherIsBetter: false },
  { value: 'gpu', label: 'GPU Frame Time', unit: 'ms', higherIsBetter: false },
];

// Distinct colors for chart lines
const CHART_COLORS = [
  '#2196F3', '#FF5722', '#4CAF50', '#9C27B0', '#FF9800',
  '#00BCD4', '#E91E63', '#8BC34A', '#3F51B5', '#FFEB3B',
  '#009688', '#F44336', '#03A9F4', '#CDDC39', '#673AB7',
  '#FFC107', '#607D8B', '#795548', '#00E676', '#FF6D00',
];

/**
 * Performance Trends tab.
 * Shows line charts of FPS/memory/CPU/GPU metrics per context (zone) over CLs.
 */
function PerformanceTrends({ api, filters, onFiltersChange }) {
  const [metric, setMetric] = useState('fps');
  const [trendData, setTrendData] = useState([]);
  const [clDates, setClDates] = useState([]);
  const [deviceComparison, setDeviceComparison] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clDatesLoading, setClDatesLoading] = useState(false);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [error, setError] = useState(null);

  // Resolved filter values from FilterBar
  const [resolved, setResolved] = useState(null);

  const handleResolvedFilters = useCallback((r) => {
    setResolved(r);
  }, []);

  // Fetch trend data when resolved filters or metric changes
  useEffect(() => {
    if (!resolved) return;
    let cancelled = false;

    async function fetchTrends() {
      setLoading(true);
      setError(null);
      try {
        const result = await api.getPerformanceTrends(
          resolved.effectiveDays, resolved.filters, metric,
          resolved.startDate, resolved.endDate
        );
        if (!cancelled) {
          setTrendData(result.data || []);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchTrends();
    return () => { cancelled = true; };
  }, [api, resolved, metric]);

  // Fetch CL dates
  useEffect(() => {
    if (!resolved) return;
    let cancelled = false;

    async function fetchClDates() {
      setClDatesLoading(true);
      try {
        const result = await api.getPerformanceChangelistDates(
          resolved.effectiveDays, resolved.filters,
          resolved.startDate, resolved.endDate
        );
        if (!cancelled) setClDates(result.data || []);
      } catch (err) {
        console.warn('Failed to load CL dates:', err.message);
      } finally {
        if (!cancelled) setClDatesLoading(false);
      }
    }

    fetchClDates();
    return () => { cancelled = true; };
  }, [api, resolved]);

  // Fetch device comparison
  useEffect(() => {
    if (!resolved) return;
    let cancelled = false;

    async function fetchDevices() {
      setDeviceLoading(true);
      try {
        const result = await api.getDeviceComparison(
          resolved.effectiveDays, resolved.filters,
          resolved.startDate, resolved.endDate
        );
        if (!cancelled) setDeviceComparison(result.data || []);
      } catch (err) {
        console.warn('Failed to load device comparison:', err.message);
      } finally {
        if (!cancelled) setDeviceLoading(false);
      }
    }

    fetchDevices();
    return () => { cancelled = true; };
  }, [api, resolved]);

  // Transform trend data into Recharts format: [{ changelist, context1: val, context2: val, ... }]
  const { chartData, contexts } = useMemo(() => {
    if (!trendData.length) return { chartData: [], contexts: [] };

    const ctxSet = new Set();
    const clMap = new Map();

    for (const row of trendData) {
      ctxSet.add(row.context);
      if (!clMap.has(row.changelist)) {
        clMap.set(row.changelist, { changelist: row.changelist });
      }
      clMap.get(row.changelist)[row.context] = Math.round(row.metricAvg * 100) / 100;
    }

    const contexts = Array.from(ctxSet).sort();
    const chartData = Array.from(clMap.values());

    return { chartData, contexts };
  }, [trendData]);

  const metricInfo = METRIC_OPTIONS.find(m => m.value === metric) || METRIC_OPTIONS[0];

  return (
    <div className="perf-trends">
      <div className="perf-trends-header">
        <h2>Performance Trends</h2>
        <p className="perf-trends-description">
          Track FPS, memory, CPU, and GPU metrics per zone across changelists.
        </p>
      </div>

      <div className="perf-trends-controls">
        <FilterBar
          api={api}
          filters={filters}
          onFiltersChange={onFiltersChange}
          onResolvedFilters={handleResolvedFilters}
        />
        <div className="perf-trends-metric-row">
          <div className="filter-bar-item">
            <label htmlFor="perf-metric">Metric:</label>
            <select
              id="perf-metric"
              value={metric}
              onChange={(e) => setMetric(e.target.value)}
            >
              {METRIC_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <span className="perf-trends-context-count">
            {contexts.length} zone{contexts.length !== 1 ? 's' : ''} with data
          </span>
        </div>
      </div>

      {error && (
        <div className="perf-trends-error">
          <p>Failed to load data: {error}</p>
        </div>
      )}

      {/* Main trend chart */}
      <div className="perf-trends-chart-section">
        <h3>{metricInfo.label} by Zone</h3>
        {loading ? (
          <div className="perf-trends-loading">Loading trend data...</div>
        ) : chartData.length === 0 ? (
          <div className="perf-trends-empty">No data available for the selected filters.</div>
        ) : (
          <ResponsiveContainer width="100%" height={450}>
            <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis
                dataKey="changelist"
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={80}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 12 }}
                label={{ value: metricInfo.unit, angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
              />
              <Tooltip
                contentStyle={{ fontSize: 12, maxHeight: 300, overflowY: 'auto' }}
                labelFormatter={(cl) => `CL ${cl}`}
                formatter={(value) => [value?.toFixed(1), '']}
              />
              <Legend
                wrapperStyle={{ fontSize: 11, maxHeight: 80, overflowY: 'auto' }}
              />
              {contexts.map((ctx, i) => (
                <Line
                  key={ctx}
                  type="monotone"
                  dataKey={ctx}
                  stroke={CHART_COLORS[i % CHART_COLORS.length]}
                  dot={false}
                  strokeWidth={1.5}
                  connectNulls
                />
              ))}
              {metric === 'fps' && (
                <ReferenceLine y={60} stroke="#f44336" strokeDasharray="5 5" label="60 FPS" />
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Device comparison */}
      <div className="perf-trends-chart-section">
        <h3>Device Comparison</h3>
        {deviceLoading ? (
          <div className="perf-trends-loading">Loading device data...</div>
        ) : deviceComparison.length === 0 ? (
          <div className="perf-trends-empty">No device data available.</div>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(300, deviceComparison.length * 50)}>
            <BarChart
              data={deviceComparison}
              layout="vertical"
              margin={{ top: 10, right: 30, left: 120, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis
                type="category"
                dataKey="device"
                tick={{ fontSize: 12 }}
                width={110}
              />
              <Tooltip
                contentStyle={{ fontSize: 12 }}
                formatter={(value, name) => [value?.toFixed(1), name]}
              />
              <Legend />
              <Bar dataKey="fpsLow" fill="#2196F3" name="FPS Low" />
              <Bar dataKey="fpsAvg" fill="#00BCD4" name="FPS Avg" />
              <Bar dataKey="fpsHigh" fill="#E91E63" name="FPS High" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Changelist dates table */}
      <div className="perf-trends-chart-section">
        <ChangelistDates data={clDates} loading={clDatesLoading} />
      </div>
    </div>
  );
}

export default PerformanceTrends;
